package org.example.project.dashboard

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import org.example.project.enginechat.userDisplayName
import org.example.project.enginekalender.loadKalenderBookings
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.enginevertrieb.LinkedInFlowApi
import org.example.project.service.RollenService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private val reminderJson = Json { ignoreUnknownKeys = true; encodeDefaults = false }

private fun reminderKey(booking: All) = "${booking.id}|${booking.datum}|${booking.uhrzeitVon}"
private fun reminderStart(booking: All): Double = kotlin.js.Date("${booking.datum}T${booking.uhrzeitVon}:00").getTime()
private fun reminderEnd(booking: All): Double = kotlin.js.Date("${booking.datum}T${booking.uhrzeitBis}:00").getTime()
private fun reminderPhaseKey(booking: All, now: Double) = reminderKey(booking) +
    if (now >= reminderStart(booking)) "|running" else ""

@Composable
internal fun KalenderReminderOverlay(art: String) {
    val user = LoginService.getUser() ?: return
    if (user.id.isBlank()) return
    val storageKey = "lumatica-calendar-reminders:$art:${user.firmenId}:${user.id}"
    var reminders by remember(storageKey) {
        mutableStateOf(runCatching {
            reminderJson.decodeFromString<List<All>>(window.localStorage.getItem(storageKey) ?: "[]")
        }.getOrDefault(emptyList()))
    }
    var bookings by remember(storageKey) { mutableStateOf<List<All>>(emptyList()) }
    var now by remember(storageKey) { mutableStateOf(kotlin.js.Date.now()) }
    val scope = rememberCoroutineScope()
    var opening by remember { mutableStateOf(false) }
    val liveVersion = GlobalManager.jsonDomainVersion("kalender")

    LaunchedEffect(storageKey, liveVersion) {
        while (true) {
            try { bookings = loadKalenderBookings(art, user.id) }
            catch (e: CancellationException) { throw e }
            catch (_: Exception) { }
            now = kotlin.js.Date.now()
            delay(30_000)
        }
    }
    LaunchedEffect(storageKey) {
        while (true) { now = kotlin.js.Date.now(); delay(1_000) }
    }

    val booking = bookings.filter {
        val start = reminderStart(it)
        val end = reminderEnd(it)
        val state = reminders.firstOrNull { saved -> saved.id == reminderPhaseKey(it, now) }
        val next = state?.kalenderErinnerungAm?.toDoubleOrNull() ?: if (now >= start) start else start - 15 * 60_000
        it.art in listOf("Termin", "Microsoft Termin") && !it.urlaub &&
            it.zustand !in listOf(ZustandManager.Termin.STORNIERT, ZustandManager.Termin.STATTGEFUNDEN) &&
            start.isFinite() && end.isFinite() && now >= next && now < end && state?.kalenderErinnerungBestaetigt != true
    }.minByOrNull { reminderStart(it) } ?: return
    val key = reminderPhaseKey(booking, now)
    val state = reminders.firstOrNull { it.id == key } ?: All(id = key)
    fun rememberResponse(response: All) {
        reminders = reminders.filterNot { it.id == key } + response
        runCatching { window.localStorage.setItem(storageKey, reminderJson.encodeToString(reminders)) }
    }
    fun acknowledge() = rememberResponse(state.copy(kalenderErinnerungBestaetigt = true))
    val remaining = kotlin.math.ceil((reminderStart(booking) - now) / 60_000).toInt().coerceAtLeast(0)
    val running = now >= reminderStart(booking)
    val elapsed = kotlin.math.floor((now - reminderStart(booking)) / 60_000).toInt().coerceAtLeast(0)
    val call = booking.terminArt == "Call"
    val appointment = booking.terminArt == "Termin"
    val title = if (call) "Call-Termin" else "Termin"
    fun openAppointment() {
        if (opening || booking.akquiseProspectId.isBlank()) return
        opening = true
        scope.launch {
            try {
                check(RollenService.hasReadPermission("LinkedInFlow")) { "Keine Berechtigung für den Flow." }
                val contact = LinkedInFlowApi.chat().linkedinKontakte.firstOrNull {
                    it.linkedinProspectId == booking.akquiseProspectId &&
                        (booking.leadQuelle.isBlank() || it.leadQuelle == booking.leadQuelle)
                } ?: error("Der zugehörige Kontakt wurde nicht gefunden.")
                val screen = if (art == "SYS" && ScreenManager.isLinkedInNode()) "SYSVertriebsDashboard" else "VertriebsDashboard"
                val query = "linkedinContact=" + (js("encodeURIComponent")(contact.id) as String)
                window.location.hash = window.location.hash.substringBefore('?') + "?screen=$screen&$query"
                acknowledge()
            } catch (e: CancellationException) { throw e }
            catch (e: Exception) { AppDialogs.alert(e.message ?: "Der Termin-Flow konnte nicht geöffnet werden.") }
            finally { opening = false }
        }
    }
    val counterpart = if (booking.akquiseProspectId.isNotBlank()) {
        booking.ansprechpartner
    } else {
        booking.teilnehmerAnzeige.split(",").map { it.trim() }
            .filter { it.isNotBlank() && !it.startsWith(userDisplayName(user) + " - ") && it != userDisplayName(user) }
            .joinToString(", ").ifBlank { booking.ansprechpartner.takeUnless { it == userDisplayName(user) }.orEmpty() }
    }
    PopupEngineCard(windowKey = "kalender-erinnerung", zIndex = 16000,
        widthPx = 620, heightPx = 490, minWidthPx = 440, minHeightPx = 350,
        centered = true, onClose = { if (!opening) acknowledge() }) {
        Div({ style { height(100.percent); minHeight(0.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); color(Color.white) } }) {
            Div({ style { flex("1 1 0"); minHeight(0.px); padding(20.px, 24.px); boxSizing("border-box"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); overflowY("auto") } }) {
                H3({ style { textAlign("center"); margin(0.px) } }) {
                    Text(if (running) "$title läuft seit $elapsed ${if (elapsed == 1) "Minute" else "Minuten"}"
                        else "$title in $remaining ${if (remaining == 1) "Minute" else "Minuten"}")
                }
            }
            Div({ style { flex("1 1 0"); minHeight(0.px); padding(12.px, 24.px); boxSizing("border-box"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); overflowY("auto") } }) {
            Div {
            if (call || appointment) {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) } }) {
                    ReminderDetail("person", "Ansprechpartner", counterpart.ifBlank { "Nicht hinterlegt" })
                    ReminderDetail("unternehmen", "Firma", booking.firma.ifBlank { "Nicht hinterlegt" })
                    ReminderDetail("kalender", "Termin", "${booking.uhrzeitVon.take(5)} - ${booking.uhrzeitBis.take(5)}")
                    if (!call) ReminderDetail("standort", "Ort", booking.ort.ifBlank { "Nicht hinterlegt" })
                }
            } else {
                P { Text(booking.bezeichnung.ifBlank { "Termin" }) }
                P { Text("Mit wem: ${counterpart.ifBlank { "Keine Kontaktangabe hinterlegt" }}") }
                P { Text("Zeit: ${booking.datum} · ${booking.uhrzeitVon}–${booking.uhrzeitBis}") }
                P { Text("Ort: ${booking.ort.ifBlank { "Nicht hinterlegt" }}") }
                P { Text("Typ: ${booking.terminArt.ifBlank { booking.art }}") }
            }
            if (!booking.beschreibung.isNullOrBlank()) Div({ style {
                backgroundColor(Color("#3F454E")); padding(12.px); whiteSpace("pre-wrap")
            } }) { Text(booking.beschreibung.orEmpty()) }
            }
            }
            Div({ style { flex("1 1 0"); minHeight(0.px); padding(20.px, 24.px); boxSizing("border-box"); display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center); flexWrap(FlexWrap.Wrap); alignContent(AlignContent.Center); gap(12.px); overflowY("auto") } }) {
                LumaticaButton("Verstanden", ::acknowledge, enabled = !opening, variant = LumaticaButtonVariant.Action)
                if (booking.akquiseProspectId.isNotBlank()) LumaticaButton(if (opening) "Öffnet …" else "Zum Termin",
                    ::openAppointment, enabled = !opening, variant = LumaticaButtonVariant.Action)
                if (!running) LumaticaButton("In 5 Minuten erinnern", {
                    rememberResponse(state.copy(kalenderErinnerungAm = (now + 5 * 60_000).toString(),
                        kalenderErinnerungWiederholungen = state.kalenderErinnerungWiederholungen + 1))
                }, enabled = !opening && state.kalenderErinnerungWiederholungen < 2 &&
                    now + 5 * 60_000 < reminderStart(booking))
            }
        }
    }
}

@Composable
private fun ReminderDetail(icon: String, label: String, value: String) {
    Div({
        attr("aria-label", "$label: $value")
        style { display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(12.px) }
    }) {
        Img(src = "images/$icon.svg", attrs = {
            attr("alt", ""); attr("title", label)
            style { width(21.px); height(21.px); flexShrink(0); property("filter", UISettings.STATE_BUTTON_ICON_FILTER) }
        })
        Span { Text(value) }
    }
}
